import { NocfoIcon } from '@components'
import { NocfoPlan, NOCFO_PLAN_OPTIONS } from '@constants'
import { getSettingsPageUrl } from '@root/constants'
import { useTheme } from '@hooks'
import { BusinessForms, IBusiness } from '@query'
import { getOptionLabelByValue } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowRight } from 'react-icons/fa'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'

interface PlanBadgeProps {
  slug: string
  business: IBusiness | null
  isNarrow: boolean
  onExpand: () => void
}

export const PlanBadge: React.FC<PlanBadgeProps> = ({ slug, business, isNarrow, onExpand }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const theme = useTheme()

  const isBillable = business?.is_billable
  const isDemoBusiness = business?.form === BusinessForms.DEMO
  const isTrialing = business?.stripe_plan_status === 'trialing'
  const isFree = business?.subscription_plan === NocfoPlan.FREE

  if (!isBillable || isDemoBusiness || isTrialing || !isFree) return null

  const planName = getOptionLabelByValue(NOCFO_PLAN_OPTIONS, business.subscription_plan, {
    t,
    fallback: '',
    fallbackToValue: true
  })

  const planSettingsUrl = getSettingsPageUrl(slug, { page: 'plan' })
  const freeBadgeLabel = t('mainMenu.planBadge.freeBadgeLabel', { plan: planName })

  return (
    <PlanBadgeWrapper $isNarrow={isNarrow}>
      {isNarrow ? (
        <PlanIconOnly type="button" $color={theme.colors.primary} onClick={onExpand}>
          <NocfoIcon />
        </PlanIconOnly>
      ) : (
        <FreePlanButton type="button" onClick={() => history.push(planSettingsUrl)}>
          <FreePlanContent>
            <PlanLabel>{freeBadgeLabel}</PlanLabel>
            <UpgradeRow>
              <span>{t('mainMenu.planBadge.upgradeCta')}</span>
              <FaArrowRight />
            </UpgradeRow>
          </FreePlanContent>
        </FreePlanButton>
      )}
    </PlanBadgeWrapper>
  )
}

const PlanBadgeWrapper = styled.div<{ $isNarrow: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${({ $isNarrow }) => ($isNarrow ? 'center' : 'flex-start')};

  > * {
    display: block;
    ${({ $isNarrow }) => !$isNarrow && 'width: 100%;'}
  }
`

const FreePlanButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray}44;
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.colors.neutralGray}11;
  color: ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.xs}rem;
  cursor: pointer;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}88;
    background: ${({ theme }) => theme.colors.primary}11;
  }
`

const FreePlanContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  width: 100%;
`

const PlanLabel = styled.div`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const UpgradeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  svg {
    width: 0.55rem;
    height: 0.55rem;
    flex-shrink: 0;
  }
`

const PlanIconOnly = styled.button<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    opacity: 0.7;
  }
`
